import React, { useState, useEffect, useRef } from 'react';
import {
  HomeIcon,
  MapIcon,
  ChartBarIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import SharedOverview from './SharedOverview';
import SharedData from './SharedData';
import SharedFusionMap from './SharedFusionMap';
import amesLogo from '../../assets/appLogoFinalLight.svg';

const TABS = [
  { id: 'overview', name: 'Overview', icon: HomeIcon },
  { id: 'map', name: 'Map', icon: MapIcon },
  { id: 'data', name: 'Data', icon: ChartBarIcon },
];

const SharedSessionView = ({ data, shareInfo, token }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [tabDropdownOpen, setTabDropdownOpen] = useState(false);
  const tabDropdownRef = useRef(null);
  
  // Profile data state (for KML/IRI data)
  const [profileData, setProfileData] = useState({
    iriProfileData: null,
    elevationProfileData: null,
    markers: null,
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);

  const { session, segments, events, company, project, projectConfig, bounds, profileDataUrl, downloads } = data;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tabDropdownRef.current && !tabDropdownRef.current.contains(e.target)) {
        setTabDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch profile data when URL is available
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!profileDataUrl) return;
      
      setProfileLoading(true);
      setProfileError(null);
      
      try {
        const response = await fetch(profileDataUrl);
        if (!response.ok) throw new Error('Failed to fetch profile data');
        
        const json = await response.json();
        
        setProfileData({
          iriProfileData: json.iriData,
          elevationProfileData: json.elevationData,
          markers: json.meta?.markers || null,
        });
      } catch (err) {
        console.error('Error loading profile data:', err);
        setProfileError(err.message);
      } finally {
        setProfileLoading(false);
      }
    };
    
    fetchProfileData();
  }, [profileDataUrl]);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setTabDropdownOpen(false);
  };

  const activeTabConfig = TABS.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Left - Logo */}
            <div className="flex items-center gap-4">
              <img
                src={amesLogo}  
                alt="Ames Engineering"
                className="h-6"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                }}
              />
              <div className="hidden sm:block h-6 w-px bg-gray-300" />
              <span className="hidden sm:block text-md font-medium text-gray-600">
                Shared Run Report
              </span>
            </div>

            {/* Right - Tab Dropdown + Expiration */}
            <div className="flex items-center gap-3">
              {/* Tab Dropdown */}
              <div className="relative" ref={tabDropdownRef}>
                <button
                  onClick={() => setTabDropdownOpen(!tabDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-sm text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors min-w-[120px]"
                >
                  {activeTabConfig && (
                    <activeTabConfig.icon className="h-4 w-4 text-gray-500" />
                  )}
                  <span>{activeTabConfig?.name || 'Select'}</span>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 ml-auto transition-transform ${tabDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {tabDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-sm shadow-lg border border-gray-200 z-50 overflow-hidden">
                    {TABS.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => handleTabClick(tab.id)}
                        className={`w-full px-3 py-2.5 text-left flex items-center gap-2 transition-colors ${
                          activeTab === tab.id 
                            ? 'bg-red-50 border-l-2 border-red-600' 
                            : 'border-l-2 border-transparent hover:bg-gray-50'
                        }`}
                      >
                        <tab.icon className={`h-4 w-4 ${activeTab === tab.id ? 'text-red-600' : 'text-gray-500'}`} />
                        <span className={`text-sm font-medium ${activeTab === tab.id ? 'text-red-700' : 'text-gray-900'}`}>
                          {tab.name}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Expiration - hidden on mobile */}
              {shareInfo?.expires_at && (
                <div className="hidden sm:flex items-center gap-1.5 text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-1 rounded-sm">
                  <span>Expires {formatDate(shareInfo.expires_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tab Content */}
      <main className="flex-1">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <SharedOverview
            session={session}
            segments={segments}
            events={events}
            company={company}
            project={project}
            projectConfig={projectConfig}
            shareInfo={shareInfo}
            downloads={downloads}
          />
        )}

        {/* Map Tab */}
        {activeTab === 'map' && (
          <div className="h-[calc(100vh-56px)]">
            <SharedFusionMap
              sessionId={session?.session_id}
              segments={segments}
              events={events}
              bounds={bounds}
              session={session}
              projectConfig={projectConfig}
              iriProfileData={profileData.iriProfileData}
              elevationProfileData={profileData.elevationProfileData}
              kmlLoading={profileLoading}
              kmlError={profileError}
              kmlMarkers={profileData.markers}
              readOnly={true}
            />
          </div>
        )}

        {/* Data Tab */}
        {activeTab === 'data' && (
          <SharedData
            segments={segments}
            events={events}
            projectConfig={projectConfig}
          />
        )}
      </main>
    </div>
  );
};

export default SharedSessionView;